import { useMemo } from 'react';
import { ALL_LESSONS } from '@chessmate/lessons';
import type { Lesson } from '@chessmate/lessons';
import { conceptExplanation } from '@chessmate/ai-coach';
import { Button, LessonCard } from '@chessmate/ui';
import { useProgressStore } from '../state/progress-store.js';
import { useT } from '../i18n/useT.js';
import { PageHeader, PANEL_CLASS } from '../components/PageHeader.js';
import { ScreenLayout } from '../components/ScreenLayout.js';
import { enabledGames } from '../edition/index.js';
import { conceptVisible } from '../lib/lesson-games.js';
import { useRemoteStages } from '../lib/remote-stage-store.js';

export function LearnScreen({
  onOpenLesson,
  onExit,
}: {
  onOpenLesson: (lessonId: string) => void;
  onExit: () => void;
}) {
  const best = useProgressStore((s) => s.best);

  const { t } = useT('navigation');
  const { t: tCommon } = useT('common');

  // The operator's remote stages (docs/proposals/remote-game-stages.md) can promote or hide a game after this screen mounted: follow them.
  const remoteStages = useRemoteStages();

  const groups = useMemo(() => {
    // A game's lessons show only while the game does (edition, build list, stage incl. the remote stage, beta opt-in).
    const visible = enabledGames();
    const map = new Map<string, Lesson[]>();
    for (const lesson of ALL_LESSONS) {
      // Lesson schema enforces `concepts.min(1)`, so the first concept is
      // guaranteed to exist — narrow with `?? ''` and the empty key gets
      // filtered into a single "uncategorised" bucket below.
      const key = lesson.concepts[0] ?? '';
      if (key === '' || !conceptVisible(key, visible)) continue;
      map.set(key, [...(map.get(key) ?? []), lesson]);
    }
    return [...map.entries()];
    // `remoteStages` is not read inside, but `enabledGames()` is: a change in it must recompute the groups.
  }, [remoteStages]);

  return (
    <ScreenLayout
      width="wide"
      data-testid="learn-screen"
      data-appdom-scope="learn"
    >
      <PageHeader
        title={t('learn')}
        titleTestId="learn-title"
        hero="/art/step-board.webp"
        subtitle={t('learn-subtitle')}
        action={
          /* Pinned by learn/quirks/i18n flows, including the translated text. */
          <Button
            variant="secondary"
            data-testid="learn-back"
            data-appdom-id="learn-back"
            data-appdom-type="Button"
            onClick={onExit}
          >
            {tCommon('back-to-home')}
          </Button>
        }
      />
      {groups.map(([concept, lessons]) => {
        const blurb = conceptExplanation(concept);
        return (
          <section key={concept} className={`${PANEL_CLASS} p-5 sm:p-7 lg:p-8`} data-testid={`learn-group-${concept}`}>
            <h2 className="font-display text-h2 font-bold capitalize text-fg lg:text-4xl">
              {concept.replaceAll('-', ' ')}
            </h2>
            {blurb && <p className="mt-1 text-base text-fg-muted lg:text-lg">{blurb}</p>}
            {/* 1-col mobile → 2 tablet → 3 desktop: no dangling stragglers
                from a fixed 3-up grid (audit F7). */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-5 2xl:grid-cols-4">
              {lessons.map((lesson) => (
                // LessonCard lives in packages/ui: size it up for big screens from out here.
                <div key={lesson.id} className="flex [&>button]:min-h-[120px] [&>button]:flex-1 [&>button]:p-5 [&_h3]:text-lg lg:[&_h3]:text-xl [&_img]:h-32 lg:[&_img]:h-40">
                  <LessonCard
                    lesson={lesson}
                    best={best[lesson.id] ?? null}
                    onOpen={() => onOpenLesson(lesson.id)}
                  />
                </div>
              ))}
            </div>
          </section>
        );
      })}
    </ScreenLayout>
  );
}
